import type { DialogueTree } from './types';

/**
 * Mom. Tone: warm but exhausted, refers to herself in third person ("love mom"),
 * always one step out of date on what your job actually is, brings dad in
 * casually, makes you feel both loved and 14 years old. Always asks if you ate.
 */
export const MOM_TREE: DialogueTree = {
  contactId: 'mom',
  rootChoices: [
    {
      id: 'i-love-you',
      label: 'tell her you love her',
      playerSays: "i love you mom",
      effects: [{ type: 'rep', target: 'mom', value: 6 }, { type: 'sanity', value: 4 }],
      responses: [
        {
          text: "oh sweetie ❤️\n\ni love you. that just made my whole week. i\'m showing your father this. love mom.",
          followUps: [
            { id: 'love-call-sister', label: "i\'ll call her",
              playerSays: "i\'ll call her",
              effects: [{ type: 'rep', target: 'mom', value: 5 }],
              responses: [
                { text: "thank you sweetie. she just needs to hear your voice. that\'s all. love mom." },
              ],
            },
            { id: 'love-defer-sister', label: "ok mom this week. promise.",
              playerSays: "this week mom. promise.",
              responses: [
                { text: "you said that last week sweetie. but. i believe you. love mom." },
              ],
            },
          ],
        },
        {
          text: "❤️❤️❤️\n\nyour father just walked in. i told him you said love. he\'s pretending he didn\'t hear me but his eyes got wet. love mom.",
          followUps: [
            { id: 'love-tell-dad', label: "tell dad i love him too",
              playerSays: "tell dad i love him too",
              effects: [{ type: 'rep', target: 'mom', value: 4 }, { type: 'sanity', value: 3 }],
              responses: [
                { text: "i told him.\n\nhe nodded. then went out to look at the lawn. that\'s a yes. love mom." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'check-on-her',
      label: 'check in on mom',
      playerSays: "how are you doing mom",
      effects: [{ type: 'rep', target: 'mom', value: 3 }],
      responses: [
        {
          text: "oh you remembered to ask, that\'s sweet sweetie.\n\ni\'m fine. the new pills make me sleepy but i\'m fine. don\'t worry. love mom.",
          followUps: [
            { id: 'check-mom-pills', label: "what are the new pills",
              playerSays: "wait, what new pills",
              effects: [{ type: 'rep', target: 'mom', value: 3 }],
              responses: [
                { text: "oh nothing nothing. for the blood pressure. dr. patel says it\'s nothing. i\'ll tell you everything when you visit. love mom." },
              ],
            },
          ],
        },
        {
          text: "tired sweetie. but in a good way. went to bridge. cynthia bid 4 spades on a 6 count. i can\'t talk to her anymore. love mom.",
        },
        {
          text: "i\'m okay. i think about you a lot. don\'t take that as guilt. it\'s just facts. love mom.",
          effects: [{ type: 'sanity', value: 2 }],
        },
      ],
    },

    {
      id: 'tell-her-about-money',
      label: 'tell her you made money',
      playerSays: "i made money this week mom. real money.",
      responses: [
        {
          text: "oh sweetie that\'s so nice ❤️\n\nis it real money or is it on a screen. i never know with you. either way, proud. love mom.",
          followUps: [
            { id: 'money-real', label: "real real. it\'s in the bank.",
              playerSays: "real real. in the bank.",
              effects: [{ type: 'rep', target: 'mom', value: 5 }],
              responses: [
                { text: "well then i can stop worrying. for one minute. then i\'ll start worrying again. that\'s the deal. love mom." },
              ],
            },
            { id: 'money-screen', label: "on a screen. but it\'s real on the screen.",
              playerSays: "on a screen. but it\'s real on the screen.",
              responses: [
                { text: "okay. okay. i don\'t understand any of this. but i\'m proud anyway. love mom." },
              ],
            },
          ],
        },
        {
          text: "wonderful sweetie. is it the kind we can spend or is it the kind that disappears.\n\ni ask because janet\'s son had a lot of one and then it became the other. love mom.",
        },
      ],
    },

    {
      id: 'tell-loss',
      label: 'tell her you lost a lot',
      playerSays: "i had a bad week mom. lost a lot.",
      effects: [{ type: 'sanity', value: 4 }, { type: 'rep', target: 'mom', value: 4 }],
      responses: [
        {
          text: "oh sweetie.\n\ndo you need money. i have some saved. i don\'t have a lot but i have some. don\'t tell your father.\n\nlove mom.",
          followUps: [
            { id: 'loss-need-money-yes', label: "i\'m okay mom. i don\'t need it.",
              playerSays: "i\'m okay mom. don\'t need it. but thank you.",
              effects: [{ type: 'rep', target: 'mom', value: 6 }, { type: 'sanity', value: 5 }],
              responses: [
                { text: "okay sweetie. i\'m here. always here. you call when you need anything. soup is in the freezer. love mom." },
              ],
            },
            { id: 'loss-need-money-no', label: "actually mom… could you spot me $500?",
              playerSays: "actually mom… could you spot me $500?",
              effects: [{ type: 'cash', value: 500 }, { type: 'rep', target: 'mom', value: -3 }, { type: 'note', value: 'mom wired $500 — she will remember' }],
              responses: [
                { text: "okay. okay. it\'s on its way. i didn\'t tell your father. you and i will figure this out. love mom." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'sister-update',
      label: 'ask about your sister',
      playerSays: "how is she",
      responses: [
        {
          text: "she\'s engaged sweetie!! i told you on facebook. you didn\'t click the heart. she noticed. she\'s pretending she didn\'t notice but she noticed. love mom.",
          followUps: [
            { id: 'sister-react', label: "i\'ll call her tonight",
              playerSays: "i\'ll call her tonight. i swear",
              effects: [{ type: 'rep', target: 'mom', value: 5 }],
              responses: [
                { text: "thank you sweetie. she said yes by the way. to the engagement. just so you know what to congratulate her on. love mom." },
              ],
            },
          ],
        },
        {
          text: "she\'s good. she asks about you. she also tells me to stop telling her you ask about her. so. it\'s complicated. love mom.",
        },
      ],
    },

    {
      id: 'food',
      label: "tell her you ate today",
      playerSays: "i ate today mom",
      effects: [{ type: 'rep', target: 'mom', value: 3 }],
      responses: [
        {
          text: "good sweetie. WHAT did you eat. i want a list. love mom.",
          followUps: [
            { id: 'food-real', label: "actual food. eggs. salad. not just coffee.",
              playerSays: "eggs. salad. not just coffee for once.",
              effects: [{ type: 'rep', target: 'mom', value: 4 }, { type: 'sanity', value: 2 }],
              responses: [
                { text: "i\'m so proud. proud of you for eggs. that\'s where we are. love mom." },
              ],
            },
            { id: 'food-fake', label: "uber eats. but it counts.",
              playerSays: "uber eats. it counts mom.",
              responses: [
                { text: "it does NOT count. but. okay. baby steps. love mom." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'news',
      label: "did you see me on the news?",
      playerSays: "did you see me on the news",
      responses: [
        {
          text: "the news?? sweetie. were you supposed to be on the news.\n\nwhat happened. should i be worried. i\'m calling cousin david. love mom.",
          followUps: [
            { id: 'news-good', label: "no it was good. i was on a panel.",
              playerSays: "no no it was good. i was on a panel.",
              effects: [{ type: 'rep', target: 'mom', value: 5 }],
              responses: [
                { text: "ohhh wonderful sweetie. i\'ll find it on the youtube. i\'ll show your father. he won\'t admit he\'s proud but he will be. love mom." },
              ],
            },
            { id: 'news-bad', label: "it was kind of bad.",
              playerSays: "it was kind of bad.",
              effects: [{ type: 'rep', target: 'mom', value: -2 }, { type: 'sanity', value: -3 }],
              responses: [
                { text: "okay. okay. don\'t tell your father. i will find out what happened from janet who will find out from her son. love mom." },
              ],
            },
          ],
        },
      ],
    },

    {
      id: 'rage',
      label: 'snap at her',
      destructive: true,
      playerSays: "mom can you NOT for one second",
      effects: [{ type: 'rep', target: 'mom', value: -10 }, { type: 'sanity', value: -8 }],
      responses: [
        {
          text: "sweetie.\n\nthat\'s not how we talk to each other.\n\ni know you\'re stressed. but. that\'s not how. love mom.",
          followUps: [
            { id: 'rage-apologize', label: "i\'m sorry. i\'m so sorry.",
              playerSays: "i\'m sorry. i\'m so sorry.",
              effects: [{ type: 'rep', target: 'mom', value: 8 }, { type: 'sanity', value: 4 }],
              responses: [
                { text: "thank you for saying it sweetie. i love you. i forgive you immediately. you should not be apologizing to your mother by text. but i love you. love mom." },
              ],
            },
          ],
        },
      ],
    },

    // ─── Buy mom a gift ─────────────────────────────────────────
    {
      id: 'buy-mom-gift',
      label: 'send mom flowers (\$60)',
      playerSays: "[wired \$60 for the florist]",
      effects: [
        { type: 'cash', value: -60 },
        { type: 'rep', target: 'mom', value: 8 },
        { type: 'sanity', value: 3 },
        { type: 'note', value: '-$60 flowers for mom. mom rep +8.' },
      ],
      responses: [
        { text: "OH SWEETIE.\n\nyou didn\'t have to.\n\nthe lady at the door was so nice. i told her all about you. she pretended to listen. proud of you. love mom." },
        { text: "they\'re beautiful sweetie ❤️\n\ni\'m putting them in the good vase. the one your father bought me in 87. the one i pretend i don\'t know he bought.\n\nlove mom" },
      ],
    },

    // ─── Big purchase brag ─────────────────────────────────────────
    {
      id: 'tell-mom-yacht',
      label: "tell mom about the yacht",
      playerSays: "mom we have a yacht now",
      requires: (ctx) => ctx.hasOwnedYacht,
      effects: [{ type: 'rep', target: 'mom', value: 10 }],
      responses: [
        { text: "A YACHT.\n\nA YACHT, SWEETIE.\n\ni am telling janet. i am telling EVERYONE at bridge. i am telling the cashier at trader joe\'s. love mom.\n\nwhen do we go on it.\n\ndon\'t answer that. just tell me when.",
          followUps: [
            { id: 'mom-yacht-invite', label: "next month mom. all of us. dad too.",
              playerSays: "next month mom. all of us. dad too.",
              effects: [{ type: 'rep', target: 'mom', value: 12 }, { type: 'sanity', value: 6 }],
              responses: [
                { text: "your father just walked in. i told him. he said \"a what.\" then he said \"i need to sit down.\" then he sat down. he\'s smiling sweetie. he never smiles. love mom." },
              ],
            },
          ],
        },
      ],
    },

    // ─── Send mom money ─────────────────────────────────────────
    {
      id: 'send-mom-money',
      label: 'wire mom \$1,000 just because',
      playerSays: "[wired \$1,000 to mom]",
      requires: (ctx) => ctx.cash >= 1000,
      effects: [
        { type: 'cash', value: -1000 },
        { type: 'rep', target: 'mom', value: 12 },
        { type: 'sanity', value: 5 },
        { type: 'note', value: '-$1,000 wire to mom. mom rep +12. she will not stop crying.' },
      ],
      responses: [
        { text: "sweetie WHAT.\n\nwhat is this. WHY. is this real. did you press the wrong button. love mom.\n\n…ok i looked. it cleared. sweetie. i\'m crying at a starbucks. they\'re going to ask me to leave. love mom." },
      ],
    },
  ],
};
